import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { track } from '../utils/analytics';
import { invalidateDashboardPlans } from '../utils/dashboardInvalidation';
import ProtectedRoute from '../components/ProtectedRoute';
import NavBar from '../components/NavBar';
import DashboardSidebar from '../components/DashboardSidebar';

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemTitle, itemType, language }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          maxWidth: '480px',
          width: '90%',
          padding: '0',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #E2E3E9'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontFamily: 'Baloo 2',
            fontWeight: '600',
            color: '#0D5449',
            textTransform: 'capitalize'
          }}>
            {itemType === 'quiz' ? 'Delete Quiz' : t('delete_lesson_plan', language)}
          </h2>
        </div>

        {/* Body */}
        <div style={{
          padding: '24px',
        }}>
          <p style={{
            margin: 0,
            fontSize: '1rem',
            fontFamily: 'Baloo 2',
            color: '#666',
            lineHeight: '1.5',
            marginBottom: '8px'
          }}>
            {itemType === 'quiz' ? 'Are you sure you want to delete this quiz?' : t('delete_confirmation_message', language)}
          </p>
          <p style={{
            margin: 0,
            fontSize: '0.875rem',
            fontFamily: 'Baloo 2',
            color: '#999',
            fontStyle: 'italic',
            fontWeight: '500'
          }}>
            "{itemTitle}"
          </p>
          <p style={{
            margin: '16px 0 0 0',
            fontSize: '0.875rem',
            fontFamily: 'Baloo 2',
            color: '#DC2626',
            fontWeight: '500'
          }}>
            {t('delete_warning', language)}
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 24px 24px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#F3F4F6',
              border: 'none',
              borderRadius: '8px',
              color: '#374151',
              fontSize: '0.9375rem',
              fontFamily: 'Baloo 2',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#E5E7EB'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F3F4F6'}
          >
            {t('cancel', language)}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 24px',
              background: '#DC2626',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '0.9375rem',
              fontFamily: 'Baloo 2',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#DC2626'}
          >
            {t('delete', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

// Lesson Plan Card Component
const LessonPlanCard = ({ plan, onClick, onDeleteClick, disabled }) => {
  const { language } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  
  const getLanguageTag = () => {
    return 'ʻŌlelo';
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDeleteClick(plan);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div 
      onClick={disabled ? undefined : onClick}
      style={{
        flex: '1 1 0', 
        minWidth: '280px',
        maxWidth: '100%',
        padding: '1rem', 
        background: '#FFFCF9', 
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', 
        borderRadius: '0.75rem', 
        border: '1px solid #BFBBB9', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        gap: '1rem', 
        display: 'flex',
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.8 : 1,
        transition: 'transform 0.2s, box-shadow 0.2s',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0px 4px 12px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0px 2px 8px rgba(0, 0, 0, 0.1)';
      }}
    >
      <div style={{width: '100%', flexDirection: 'column', gap: '0.5rem', display: 'flex'}}>
        <div style={{width: '100%', justifyContent: 'space-between', alignItems: 'center', display: 'flex'}}>
          <div style={{flex: 1, color: 'black', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '400', wordWrap: 'break-word'}}>
            {plan.standard || t('standard_code', language)}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative'}}>
            <div 
              onClick={handleMenuClick}
              style={{
                width: '1.5rem', 
                height: '1.5rem', 
                position: 'relative', 
                flexShrink: 0, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                background: '#696A6E',
                boxShadow: '0 -6px 0 #696A6E, 0 6px 0 #696A6E'
              }} />
            </div>
            
            {showMenu && (
              <div
                ref={menuRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '0.25rem',
                  background: 'white',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  borderRadius: '0.5rem',
                  border: '1px solid #E2E3E9',
                  minWidth: '120px',
                  zIndex: 1000,
                  padding: '0.25rem 0'
                }}
              >
                <div
                  onClick={handleDelete}
                  style={{
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    color: '#DC2626',
                    fontSize: '0.875rem',
                    fontFamily: 'Baloo 2',
                    fontWeight: '400',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {t('delete', language)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{width: '100%', color: 'black', fontSize: '1.5rem', fontFamily: 'Baloo 2', fontWeight: '500', lineHeight: '1.3', wordWrap: 'break-word'}}>
          {plan.title || t('understanding_computer_parts', language)}
        </div>
        <div style={{width: '100%', color: '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: '1.4', wordWrap: 'break-word'}}>
          {plan.standard_description || plan.description || t('lesson_description', language)}
        </div>
      </div>
      <div style={{width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '0.5rem', display: 'flex', flexWrap: 'wrap'}}>
        <div style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#F2F3F8', 
          borderRadius: '0.25rem', 
          border: '1px solid #E2E3E9', 
          justifyContent: 'center', 
          alignItems: 'center', 
          display: 'flex'
        }}>
          <div style={{
            color: '#696A6E', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {plan.grade || 'K-2'}
          </div>
        </div>
        <div style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#F2F3F8', 
          borderRadius: '0.25rem', 
          border: '1px solid #E2E3E9', 
          justifyContent: 'center', 
          alignItems: 'center', 
          display: 'flex'
        }}>
          <div style={{
            color: '#696A6E', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {getLanguageTag()}
          </div>
        </div>
        <div style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#F2F3F8', 
          borderRadius: '0.25rem', 
          border: '1px solid #E2E3E9', 
          justifyContent: 'center', 
          alignItems: 'center', 
          display: 'flex'
        }}>
          <div style={{
            color: '#696A6E', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {plan.subject || 'Computer Science'}
          </div>
        </div>
      </div>
    </div>
  );
};

// HIDDEN: Quiz Card Component temporarily disabled
/* const QuizCard = ({ quiz, onClick, onDeleteClick }) => {
  const { language } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDeleteClick(quiz);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div 
      onClick={onClick}
      style={{
        flex: '1 1 0', 
        minWidth: '280px',
        maxWidth: '100%',
        padding: '1rem', 
        background: '#F9FAFF', 
        boxShadow: '0px 2px 8px rgba(99, 102, 241, 0.15)', 
        borderRadius: '0.75rem', 
        border: '1px solid #A5B4FC', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'flex-start', 
        gap: '1rem', 
        display: 'flex',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0px 4px 12px rgba(99, 102, 241, 0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0px 2px 8px rgba(99, 102, 241, 0.15)';
      }}
    >
      <div style={{width: '100%', flexDirection: 'column', gap: '0.5rem', display: 'flex'}}>
        <div style={{width: '100%', justifyContent: 'space-between', alignItems: 'center', display: 'flex'}}>
          <div style={{flex: 1, color: '#6366F1', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '500', wordWrap: 'break-word'}}>
            {quiz.standards && quiz.standards.length > 0 ? quiz.standards[0].standard : 'Quiz'}
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative'}}>
            <div 
              onClick={handleMenuClick}
              style={{
                width: '1.5rem', 
                height: '1.5rem', 
                position: 'relative', 
                flexShrink: 0, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                background: '#6366F1',
                boxShadow: '0 -6px 0 #6366F1, 0 6px 0 #6366F1'
              }} />
            </div>
            
            {showMenu && (
              <div
                ref={menuRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '0.25rem',
                  background: 'white',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  borderRadius: '0.5rem',
                  border: '1px solid #E2E3E9',
                  minWidth: '120px',
                  zIndex: 1000,
                  padding: '0.25rem 0'
                }}
              >
                <div
                  onClick={handleDelete}
                  style={{
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    color: '#DC2626',
                    fontSize: '0.875rem',
                    fontFamily: 'Baloo 2',
                    fontWeight: '400',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  {t('delete', language)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{width: '100%', color: '#4F46E5', fontSize: '1.5rem', fontFamily: 'Baloo 2', fontWeight: '500', lineHeight: '1.3', wordWrap: 'break-word'}}>
          {quiz.title || 'Untitled Quiz'}
        </div>
        {quiz.description && (
          <div style={{width: '100%', color: '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: '400', lineHeight: '1.4', wordWrap: 'break-word'}}>
            {quiz.description}
          </div>
        )}
      </div>
      <div style={{width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '0.5rem', display: 'flex', flexWrap: 'wrap'}}>
        {quiz.grade && (
          <div style={{
            paddingLeft: '0.5rem', 
            paddingRight: '0.5rem', 
            paddingTop: '0.25rem', 
            paddingBottom: '0.25rem', 
            background: '#EEF2FF', 
            borderRadius: '0.25rem', 
            border: '1px solid #C7D2FE', 
            justifyContent: 'center', 
            alignItems: 'center', 
            display: 'flex'
          }}>
            <div style={{
              color: '#6366F1', 
              fontSize: '0.75rem', 
              fontFamily: 'Azeret Mono', 
              fontWeight: '600', 
              textTransform: 'uppercase', 
              lineHeight: '1', 
              whiteSpace: 'nowrap'
            }}>
              {quiz.grade}
            </div>
          </div>
        )}
        {quiz.subject && (
          <div style={{
            paddingLeft: '0.5rem', 
            paddingRight: '0.5rem', 
            paddingTop: '0.25rem', 
            paddingBottom: '0.25rem', 
            background: '#EEF2FF', 
            borderRadius: '0.25rem', 
            border: '1px solid #C7D2FE', 
            justifyContent: 'center', 
            alignItems: 'center', 
            display: 'flex'
          }}>
            <div style={{
              color: '#6366F1', 
              fontSize: '0.75rem', 
              fontFamily: 'Azeret Mono', 
              fontWeight: '600', 
              textTransform: 'uppercase', 
              lineHeight: '1', 
              whiteSpace: 'nowrap'
            }}>
              {quiz.subject === 'cs' ? 'CS' : quiz.subject === 'math' ? 'MATH' : quiz.subject.toUpperCase()}
            </div>
          </div>
        )}
        <div style={{
          paddingLeft: '0.5rem', 
          paddingRight: '0.5rem', 
          paddingTop: '0.25rem', 
          paddingBottom: '0.25rem', 
          background: '#EEF2FF', 
          borderRadius: '0.25rem', 
          border: '1px solid #C7D2FE', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '0.25rem',
          display: 'flex'
        }}>
          <img
            src="/assets/Solid/Interface/AI.svg"
            alt="AI Assisted"
            style={{ width: '0.875rem', height: '0.875rem', flexShrink: 0 }}
          />
          <div style={{
            color: '#6366F1', 
            fontSize: '0.75rem', 
            fontFamily: 'Azeret Mono', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            lineHeight: '1', 
            whiteSpace: 'nowrap'
          }}>
            {t('ai_assisted', language)}
          </div>
        </div>
      </div>
    </div>
  );
}; */

function MyPlansContent() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const [completedPlans, setCompletedPlans] = useState([]);
  const [inProgressPlans, setInProgressPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('completed'); // 'completed' | 'in_progress'
  const [deleteLessonModal, setDeleteLessonModal] = useState({ isOpen: false, plan: null });
  const [resumingPlanId, setResumingPlanId] = useState(null);

  // Filters and sort (same as Dashboard)
  const [filters, setFilters] = useState({ subject: null, language: null, grade: null });
  const [sortBy, setSortBy] = useState('most_recent');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [inProgressRes, completedRes] = await Promise.all([
          fetch('/api/lesson-plans/in-progress/', {
            headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
          }),
          fetch('/api/lesson-plans/completed/', {
            headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
          }),
        ]);

        if (inProgressRes.ok) {
          const data = await inProgressRes.json();
          setInProgressPlans(data.plans || []);
        }
        if (completedRes.ok) {
          const data = await completedRes.json();
          setCompletedPlans(data.plans || []);
        }
      } catch (e) {
        console.error('Failed to load data', e);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchData();
  }, [token]);

  const getAllPlans = () => [...completedPlans, ...inProgressPlans];

  const planMatchesGrade = (plan, gradeFilter) => {
    if (!gradeFilter) return true;
    if (gradeFilter === 'High School') {
      return plan.grade === 'High School' || plan.grade === 'HS' || (plan.grade && String(plan.grade).startsWith('High School:'));
    }
    if (gradeFilter === 'Middle School') {
      return plan.grade === 'Middle School' || plan.grade === 'MS';
    }
    return plan.grade === gradeFilter;
  };

  const filterPlans = (plans) => {
    return plans.filter(plan => {
      if (filters.subject && plan.subject !== filters.subject) return false;
      if (filters.language && plan.language !== filters.language) return false;
      if (filters.grade && !planMatchesGrade(plan, filters.grade)) return false;
      return true;
    });
  };

  const sortPlans = (plans) => {
    const sorted = [...plans];
    switch (sortBy) {
      case 'most_recent':
        return sorted.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at));
      case 'title_asc':
        return sorted.sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase()));
      case 'title_desc':
        return sorted.sort((a, b) => (b.title || '').toLowerCase().localeCompare((a.title || '').toLowerCase()));
      case 'status':
        return sorted.sort((a, b) => {
          if (a.status === 'completed' && b.status !== 'completed') return -1;
          if (a.status !== 'completed' && b.status === 'completed') return 1;
          return 0;
        });
      default:
        return sorted;
    }
  };

  const getGradeOptions = () => [
    'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
    'Middle School',
    'High School',
  ];

  const getLanguageOptions = () => [
    { value: 'en', label: 'English' },
    { value: 'haw', label: 'ʻŌlelo Hawaiʻi' },
  ];

  const getUniqueSubjects = () => Array.from(new Set(getAllPlans().map(p => p.subject).filter(Boolean)));

  const sortOptions = [
    { value: 'most_recent', label: t('most_recent', language) || 'Most Recent' },
    { value: 'oldest', label: t('oldest', language) || 'Oldest' },
    { value: 'title_asc', label: t('title_a_z', language) || 'Title A-Z' },
    { value: 'title_desc', label: t('title_z_a', language) || 'Title Z-A' },
    { value: 'status', label: t('status', language) || 'Status' },
  ];

  const filteredCompletedPlans = sortPlans(filterPlans(completedPlans));
  const filteredInProgressPlans = sortPlans(filterPlans(inProgressPlans));
  const displayPlans = activeTab === 'completed' ? filteredCompletedPlans : filteredInProgressPlans;

  const handleLessonClick = (plan) => {
    track('Viewed Lesson Plan', { lesson_plan_id: plan.id, screen_name: 'My Plans' });
    if (plan.status === 'completed') {
      navigate(`/view-lesson-plan?id=${plan.id}`);
      return;
    }
    handleInProgressCardClick(plan);
  };

  const handleInProgressCardClick = async (plan) => {
    track('Resume Lesson Plan', { lesson_plan_id: plan.id, screen_name: 'My Plans' });
    setResumingPlanId(plan.id);
    try {
      const res = await fetch(`/api/lesson-plans/${plan.id}/resume/`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        navigate(`/view-lesson-plan?id=${plan.id}`);
        return;
      }
      const data = await res.json();
      const resumeData = {
        id: data.id, title: data.title, subject: data.subject, grade: data.grade, standard: data.standard,
        concept: data.concept, subconcept: data.subconcept, standard_description: data.standard_description,
        language: data.language, moku: data.moku, island: data.island, current_step: data.current_step,
        lesson_plan_data: data.lesson_plan_data || {},
      };
      const generatedElements = data.generated_elements;
      const hasElements = generatedElements && (generatedElements.story || generatedElements.proverb || generatedElements.cultural_practice);
      const canResumeToElements = hasElements && data.current_step !== 'create_lesson';

      if (canResumeToElements) {
        navigate('/select-elements', {
          state: {
            resumeData, isResuming: true,
            selectedSubject: data.subject, selectedGrade: data.grade, selectedConcept: data.concept,
            selectedSubconcept: data.subconcept, selectedStandardIdentifier: data.standard,
            selectedStandardDescription: data.standard_description, selectedLanguage: data.language,
            selectedMoku: data.moku, selectedIsland: data.island, generatedElements,
          },
        });
      } else {
        navigate('/create-lesson-plan/form', { state: { resumeData, isResuming: true } });
      }
    } catch (e) {
      console.error('Failed to resume lesson plan', e);
      navigate(`/view-lesson-plan?id=${plan.id}`);
    } finally {
      setResumingPlanId(null);
    }
  };

  // HIDDEN: Quiz click handler temporarily disabled
  /* const handleQuizClick = (quiz) => {
    // Navigate to view quiz screen (view-only mode)
    navigate(`/view-quiz/${quiz.id}`);
  }; */

  const handleDeleteLessonClick = (plan) => {
    setDeleteLessonModal({ isOpen: true, plan });
  };

  // HIDDEN: Quiz delete handler temporarily disabled
  /* const handleDeleteQuizClick = (quiz) => {
    setDeleteQuizModal({ isOpen: true, quiz });
  }; */

  const handleDeleteLessonConfirm = async () => {
    if (!deleteLessonModal.plan) return;

    const planId = deleteLessonModal.plan.id;
    try {
      const response = await fetch(`/api/lesson-plans/${planId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setCompletedPlans(prev => prev.filter(plan => plan.id !== planId));
        setInProgressPlans(prev => prev.filter(plan => plan.id !== planId));
        setDeleteLessonModal({ isOpen: false, plan: null });
        invalidateDashboardPlans(token);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to delete lesson plan');
      }
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
      alert('Failed to delete lesson plan. Please try again.');
    }
  };

  // HIDDEN: Quiz delete confirmation handler temporarily disabled
  /* const handleDeleteQuizConfirm = async () => {
    if (!deleteQuizModal.quiz) return;

    const quizId = deleteQuizModal.quiz.id;
    try {
      const response = await fetch(`/api/quiz/${quizId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setQuizzes(prev => prev.filter(quiz => quiz.id !== quizId));
        setDeleteQuizModal({ isOpen: false, quiz: null });
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to delete quiz');
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Failed to delete quiz. Please try again.');
    }
  }; */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-filter-dropdown]') && !event.target.closest('[data-sort-dropdown]')) {
        setShowSubjectDropdown(false);
        setShowLanguageDropdown(false);
        setShowGradeDropdown(false);
        setShowSortDropdown(false);
      }
    };
    if (showSubjectDropdown || showLanguageDropdown || showGradeDropdown || showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSubjectDropdown, showLanguageDropdown, showGradeDropdown, showSortDropdown]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', overflow: 'hidden', background: '#F9FAFF' }}>
      <DeleteConfirmationModal
        isOpen={deleteLessonModal.isOpen}
        onClose={() => setDeleteLessonModal({ isOpen: false, plan: null })}
        onConfirm={handleDeleteLessonConfirm}
        itemTitle={deleteLessonModal.plan?.title || ''}
        itemType="lesson"
        language={language}
      />
      {/* HIDDEN: Quiz deletion modal temporarily disabled */}
      {/* <DeleteConfirmationModal
        isOpen={deleteQuizModal.isOpen}
        onClose={() => setDeleteQuizModal({ isOpen: false, quiz: null })}
        onConfirm={handleDeleteQuizConfirm}
        itemTitle={deleteQuizModal.quiz?.title || ''}
        itemType="quiz"
        language={language}
      /> */}
      <NavBar />
      <div style={{ display: 'flex', flex: 1, width: '100%', overflow: 'hidden', background: '#F9FAFF' }}>
        {/* Sidebar */}
        <div style={{ width: '18%', minWidth: '200px', maxWidth: '280px', flexShrink: 0, overflowY: 'auto' }}>
          <DashboardSidebar />
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '2rem 3rem' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <h1 style={{
                fontSize: 'clamp(2rem, 3vw, 3rem)',
                fontFamily: 'Baloo 2',
                fontWeight: '600',
                color: '#0D5449',
                margin: '0 0 0.5rem 0'
              }}>
                {t('my_plans', language)}
              </h1>
              <p style={{
                fontSize: '1.125rem',
                fontFamily: 'Baloo 2',
                color: '#666',
                margin: 0
              }}>
                All your lesson plans in one place
              </p>
            </div>

            {/* Tabs: Completed | In Progress */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #E2E3E9' }}>
              <div
                onClick={() => setActiveTab('completed')}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontFamily: 'Baloo 2',
                  fontWeight: '600',
                  color: activeTab === 'completed' ? '#0D5449' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeTab === 'completed' ? '3px solid #189F61' : '3px solid transparent',
                  marginBottom: '-2px',
                  transition: 'all 0.2s',
                }}
              >
                {t('completed_lesson_plans', language)} ({filteredCompletedPlans.length})
              </div>
              <div
                onClick={() => setActiveTab('in_progress')}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontFamily: 'Baloo 2',
                  fontWeight: '600',
                  color: activeTab === 'in_progress' ? '#0D5449' : '#666',
                  cursor: 'pointer',
                  borderBottom: activeTab === 'in_progress' ? '3px solid #189F61' : '3px solid transparent',
                  marginBottom: '-2px',
                  transition: 'all 0.2s',
                }}
              >
                {t('in_progress_lesson_plans', language)} ({filteredInProgressPlans.length})
              </div>
            </div>

            {/* Filters: Subject, Language, Grade, Sort By (same as Dashboard) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative' }} data-filter-dropdown>
                  <div style={{ padding: '0.5rem 0.5rem 0.5rem 1rem', background: filters.subject ? 'linear-gradient(131deg, #137A55 16%, #0D5449 100%)' : 'linear-gradient(131deg, #189F61 16%, #137A55 100%)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'white', fontSize: 'clamp(0.875rem, 1vw, 1rem)', fontFamily: 'Azeret Mono', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    <div onClick={() => { setShowSubjectDropdown(!showSubjectDropdown); setShowLanguageDropdown(false); setShowGradeDropdown(false); setShowSortDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                      {filters.subject ? (filters.subject === 'cs' ? 'CS' : filters.subject === 'math' ? 'MATH' : 'SCIENCE') : t('subject', language)}
                      <span style={{ width: '1rem', height: '1rem', display: 'inline-block' }}>▼</span>
                    </div>
                    {filters.subject && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, subject: null }); setShowSubjectDropdown(false); }} style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', lineHeight: 1, padding: 0, flexShrink: 0 }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }} aria-label="Clear subject">×</button>
                    )}
                  </div>
                  {showSubjectDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', background: 'white', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', borderRadius: '0.5rem', border: '1px solid #E2E3E9', minWidth: '150px', zIndex: 1000, padding: '0.25rem 0', maxHeight: '300px', overflowY: 'auto' }}>
                      <div onClick={() => { setFilters({ ...filters, subject: null }); setShowSubjectDropdown(false); }} style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: filters.subject === null ? '#189F61' : '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: filters.subject === null ? '600' : '400', backgroundColor: filters.subject === null ? '#F0FDF4' : 'transparent' }}>{t('all', language) || 'All'}</div>
                      {getUniqueSubjects().map(subject => (
                        <div key={subject} onClick={() => { setFilters({ ...filters, subject }); setShowSubjectDropdown(false); }} style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: filters.subject === subject ? '#189F61' : '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: filters.subject === subject ? '600' : '400', backgroundColor: filters.subject === subject ? '#F0FDF4' : 'transparent' }}>{subject === 'cs' ? 'CS' : subject === 'math' ? 'MATH' : 'SCIENCE'}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ position: 'relative' }} data-filter-dropdown>
                  <div style={{ padding: '0.5rem 0.5rem 0.5rem 1rem', background: filters.language ? 'linear-gradient(131deg, #137A55 16%, #0D5449 100%)' : 'linear-gradient(131deg, #189F61 16%, #137A55 100%)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'white', fontSize: 'clamp(0.875rem, 1vw, 1rem)', fontFamily: 'Azeret Mono', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    <div onClick={() => { setShowLanguageDropdown(!showLanguageDropdown); setShowSubjectDropdown(false); setShowGradeDropdown(false); setShowSortDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                      {filters.language ? (filters.language === 'en' ? 'ENGLISH' : 'ʻŌLELO HAWAIʻI') : t('language', language)}
                      <span style={{ width: '1rem', height: '1rem', display: 'inline-block' }}>▼</span>
                    </div>
                    {filters.language && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, language: null }); setShowLanguageDropdown(false); }} style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', lineHeight: 1, padding: 0, flexShrink: 0 }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }} aria-label="Clear language">×</button>
                    )}
                  </div>
                  {showLanguageDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', background: 'white', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', borderRadius: '0.5rem', border: '1px solid #E2E3E9', minWidth: '150px', zIndex: 1000, padding: '0.25rem 0' }}>
                      <div onClick={() => { setFilters({ ...filters, language: null }); setShowLanguageDropdown(false); }} style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: filters.language === null ? '#189F61' : '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: filters.language === null ? '600' : '400', backgroundColor: filters.language === null ? '#F0FDF4' : 'transparent' }}>{t('all', language) || 'All'}</div>
                      {getLanguageOptions().map(option => (
                        <div key={option.value} onClick={() => { setFilters({ ...filters, language: option.value }); setShowLanguageDropdown(false); }} style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: filters.language === option.value ? '#189F61' : '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: filters.language === option.value ? '600' : '400', backgroundColor: filters.language === option.value ? '#F0FDF4' : 'transparent' }}>{option.label}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ position: 'relative' }} data-filter-dropdown>
                  <div style={{ padding: '0.5rem 0.5rem 0.5rem 1rem', background: filters.grade ? 'linear-gradient(131deg, #137A55 16%, #0D5449 100%)' : 'linear-gradient(131deg, #189F61 16%, #137A55 100%)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'white', fontSize: 'clamp(0.875rem, 1vw, 1rem)', fontFamily: 'Azeret Mono', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    <div onClick={() => { setShowGradeDropdown(!showGradeDropdown); setShowSubjectDropdown(false); setShowLanguageDropdown(false); setShowSortDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                      {filters.grade || t('grade', language)}
                      <span style={{ width: '1rem', height: '1rem', display: 'inline-block' }}>▼</span>
                    </div>
                    {filters.grade && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, grade: null }); setShowGradeDropdown(false); }} style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', lineHeight: 1, padding: 0, flexShrink: 0 }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }} aria-label="Clear grade">×</button>
                    )}
                  </div>
                  {showGradeDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', background: 'white', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', borderRadius: '0.5rem', border: '1px solid #E2E3E9', minWidth: '150px', zIndex: 1000, padding: '0.25rem 0', maxHeight: '300px', overflowY: 'auto' }}>
                      <div onClick={() => { setFilters({ ...filters, grade: null }); setShowGradeDropdown(false); }} style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: filters.grade === null ? '#189F61' : '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: filters.grade === null ? '600' : '400', backgroundColor: filters.grade === null ? '#F0FDF4' : 'transparent' }}>{t('all', language) || 'All'}</div>
                      {getGradeOptions().map(grade => (
                        <div key={grade} onClick={() => { setFilters({ ...filters, grade }); setShowGradeDropdown(false); }} style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: filters.grade === grade ? '#189F61' : '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: filters.grade === grade ? '600' : '400', backgroundColor: filters.grade === grade ? '#F0FDF4' : 'transparent' }}>{grade}</div>
                      ))}
                    </div>
                  )}
                </div>
                {(filters.subject || filters.language || filters.grade) && (
                  <button
                    type="button"
                    onClick={() => { setFilters({ subject: null, language: null, grade: null }); setShowSubjectDropdown(false); setShowLanguageDropdown(false); setShowGradeDropdown(false); }}
                    style={{ padding: '0.5rem 0.75rem', background: 'transparent', border: '1px solid #189F61', borderRadius: '0.5rem', color: '#137A55', fontSize: '0.8125rem', fontFamily: 'Baloo 2', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F0FDF4'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto', position: 'relative' }}>
                <span style={{ color: '#696A6E', fontSize: 'clamp(0.875rem, 1vw, 1rem)', fontFamily: 'Azeret Mono', fontWeight: '600', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{t('sort_by', language)}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', position: 'relative' }} data-sort-dropdown onClick={() => { setShowSortDropdown(!showSortDropdown); setShowSubjectDropdown(false); setShowLanguageDropdown(false); setShowGradeDropdown(false); }}>
                  <span style={{ color: '#137A55', fontSize: 'clamp(0.875rem, 1vw, 1rem)', fontFamily: 'Azeret Mono', fontWeight: '600', textTransform: 'uppercase' }}>{sortOptions.find(o => o.value === sortBy)?.label || sortOptions[0].label}</span>
                  <span style={{ width: '1rem', height: '1rem', display: 'inline-block' }}>▼</span>
                  {showSortDropdown && (
                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: 'white', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', borderRadius: '0.5rem', border: '1px solid #E2E3E9', minWidth: '160px', zIndex: 1000, padding: '0.25rem 0' }}>
                      {sortOptions.map(option => (
                        <div key={option.value} onClick={() => { setSortBy(option.value); setShowSortDropdown(false); }} style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: sortBy === option.value ? '#189F61' : '#666', fontSize: '0.875rem', fontFamily: 'Baloo 2', fontWeight: sortBy === option.value ? '600' : '400', backgroundColor: sortBy === option.value ? '#F0FDF4' : 'transparent' }}>{option.label}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
                Loading...
              </div>
            ) : (
              <>
                {displayPlans.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
                    {displayPlans.map(plan => (
                      <LessonPlanCard
                        key={plan.id}
                        plan={plan}
                        onClick={() => handleLessonClick(plan)}
                        onDeleteClick={handleDeleteLessonClick}
                        disabled={activeTab === 'in_progress' && resumingPlanId === plan.id}
                      />
                    ))}
                  </div>
                )}

                {/* HIDDEN: Quizzes Section temporarily disabled */}
                {/* {filteredQuizzes.length > 0 && (
                  <div style={{ marginBottom: '3rem' }}>
                    {activeTab === 'all' && (
                      <h2 style={{
                        fontSize: '1.5rem',
                        fontFamily: 'Baloo 2',
                        fontWeight: '600',
                        color: '#4F46E5',
                        marginBottom: '1.5rem'
                      }}>
                        Quizzes
                      </h2>
                    )}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '1.5rem'
                    }}>
                      {filteredQuizzes.map(quiz => (
                        <QuizCard
                          key={quiz.id}
                          quiz={quiz}
                          onClick={() => handleQuizClick(quiz)}
                          onDeleteClick={handleDeleteQuizClick}
                        />
                      ))}
                    </div>
                  </div>
                )} */}

                {/* Empty State */}
                {displayPlans.length === 0 && (
                  <div style={{
                    padding: '4rem 2rem',
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: '1rem',
                    border: '2px dashed #E2E3E9'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
                    <h3 style={{
                      fontSize: '1.5rem',
                      fontFamily: 'Baloo 2',
                      fontWeight: '600',
                      color: '#0D5449',
                      marginBottom: '0.5rem'
                    }}>
                      {activeTab === 'completed' && 'No completed lesson plans yet'}
                      {activeTab === 'in_progress' && 'No in-progress lesson plans'}
                    </h3>
                    <p style={{
                      fontSize: '1rem',
                      fontFamily: 'Baloo 2',
                      color: '#666',
                      marginBottom: '2rem'
                    }}>
                      {activeTab === 'completed' && 'Complete a lesson plan to see it here'}
                      {activeTab === 'in_progress' && 'Start a lesson plan to see it here'}
                    </p>
                    <button
                      onClick={() => { track('Clicked Create Lesson Plan', { screen_name: 'My Plans' }); navigate('/create-lesson-plan/form'); }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(131deg, #189F61 16%, #137A55 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        fontFamily: 'Baloo 2',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      Create Lesson Plan
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyPlans() {
  return (
    <ProtectedRoute>
      <MyPlansContent />
    </ProtectedRoute>
  );
}
